import dayjs from 'dayjs';
import moment from 'moment';

import customParseFormat from 'dayjs/plugin/customParseFormat';

// Enable custom parsing plugin
dayjs.extend(customParseFormat);

const DATE_FORMATS = [
  // Year-Month formats
  'YYYY-MM',
  'MM-YYYY',
  'YYYY/MM',
  'MM/YYYY',
  'MM.YYYY',
  'YYYY.MM',

  // Month-name formats (e.g. "Jan 2020", "January 2020", "Jan, 2020")
  'MMM YYYY',
  'MMMM YYYY',
  'MMM, YYYY',
  'MMMM, YYYY',

  // Full date formats
  'DD-MM-YYYY',
  'MM-DD-YYYY',
  'YYYY-DD-MM',
  'YYYY-MM-DD',
  'DD/MM/YYYY',
  'MM/DD/YYYY',
  'YYYY/MM/DD',
  'YYYY/DD/MM',
  'DD.MM.YYYY',
  'MM.DD.YYYY',
  'YYYY.MM.DD',
  'YYYY.DD.MM',

  // ISO formats
  'YYYY-MM-DD[T]HH:mm:ss[Z]',
  'YYYY-MM-DD[T]HH:mm:ss',
  'YYYY-MM-DD[T]HH:mm:ss.SSS[Z]',
];

/**
 * Parses various date formats into a JavaScript Date object
 *
 * @param {string} str - The date string to parse
 * @returns {Date|null} Returns JavaScript Date object if valid, null if invalid
 */
export const parseDynamicDate = (str) => {
  const parseDate = (str) => {
    if (!str) return null;

    const input = (typeof str === 'number' ? str.toString() : str).trim();
    if (!input) return null;
    const lowerStr = input.toLowerCase();

    // Handle relative dates (case insensitive)
    if (lowerStr === 'today') return dayjs().toDate();
    if (lowerStr === 'yesterday') return dayjs().subtract(1, 'day').toDate();
    if (lowerStr === 'tomorrow') return dayjs().add(1, 'day').toDate();

    // Handle year-only format (e.g., "2020"). Built in local time so it
    // re-reads as January everywhere — Date.UTC rolls back a month in the Americas.
    if (/^\d{4}$/.test(input)) {
      return new Date(Number(input), 0, 1);
    }

    // Handle MM/YYYY format (e.g., "03/2018" or "10/2021")
    if (/^\d{1,2}\/\d{4}$/.test(input)) {
      const [month, year] = input.split('/');
      return dayjs(`${year}-${month.padStart(2, '0')}-01`).toDate();
    }

    // Try other common formats
    for (const format of DATE_FORMATS) {
      const parsed = dayjs(input, format, true);
      if (parsed.isValid()) {
        return parsed.toDate();
      }
    }

    // If no strict format matches, try loose parsing as a last resort
    const parsed = dayjs(input);
    return parsed.isValid() ? parsed.toDate() : null;
  };

  // External logic ensures the result is a Date instance or null
  const result = parseDate(str);
  return result instanceof Date && !isNaN(result.getTime()) ? result : null;
};

export const parseDateToGivenFormat = (date, format) => {
  //using moment to format the current day date and selected date in datePicker
  return moment(date).format(format);
};

/**
 * Formats a date to YYYY-MM-DD using local time to avoid timezone shifts
 *
 * @param {Date|string|number} date - The date to format
 * @returns {string} Returns date in YYYY-MM-DD format
 */
export const formatLocalDate = (date) => {
  const d = new Date(date);
  const year = d.getFullYear();
  const month = String(d.getMonth() + 1).padStart(2, '0');
  const day = String(d.getDate()).padStart(2, '0');
  return `${year}-${month}-${day}`;
};
